import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
    CheckoutElementsProvider,
    useCheckoutElements,
    ContactDetailsElement,
    ShippingAddressElement,
    PaymentElement,
    ExpressCheckoutElement,
} from '@stripe/react-stripe-js/checkout';
import Meta from '../components/Meta';
import { useCreateCheckoutSessionMutation } from '../slices/checkoutApiSlice';
import { getStripePromise, stripeAppearance, stripeFonts } from '../utils/stripe';
import '../assets/styles/checkout.css';

const FREE_SHIPPING_THRESHOLD = 50; // keep in sync with backend/utils/calcPrices.js

const LockIcon = ({ size = 16, color = 'currentColor' }) => (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
);

const CheckIcon = () => (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
);

const formatGBP = (pence) => `£${(pence / 100).toFixed(2)}`;

// ─────────────────────────────────────────────────────────────────────────────
// Page: starts a Checkout Session for the basket, then renders the form inside it
// Prefill for a (re)started session. The email is only prefilled for guests —
// when the server has set it (signed in), Stripe rejects any change to it.
const buildDefaults = (session, carry, guestEmail) => {
    const values = {};
    const email = carry.email || guestEmail;
    if (!session.customerEmail && email) values.email = email;
    const addr = carry.shippingAddress?.address;
    if (addr?.country) {
        const address = Object.fromEntries(Object.entries(addr).filter(([, v]) => v));
        values.shippingAddress = { address, ...(carry.shippingAddress.name ? { name: carry.shippingAddress.name } : {}) };
    }
    return values;
};

// ─────────────────────────────────────────────────────────────────────────────
const CheckoutScreen = () => {
    const navigate = useNavigate();
    const { cartItems, guestEmail } = useSelector((state) => state.cart);

    const [session, setSession] = useState(null);
    const [error, setError] = useState(null);
    // What the shopper already typed, carried into the new session when a promo is applied
    const [carry, setCarry] = useState({});
    const [createSession] = useCreateCheckoutSessionMutation();
    const stripePromise = useMemo(() => getStripePromise(), []);
    const started = useRef(false);

    const orderItems = useMemo(
        () => cartItems.map((i) => ({ product: i._id, qty: i.qty, size: i.size || null })),
        [cartItems]
    );

    // Returns null on success, or an error message
    const startSession = useCallback(async (promoCode, typed = null) => {
        try {
            const res = await createSession({ orderItems, promoCode: promoCode || undefined }).unwrap();
            if (typed) setCarry(typed);
            setSession(res);
            setError(null);
            return null;
        } catch (err) {
            return err?.data?.message || 'We couldn’t start checkout — please try again';
        }
    }, [createSession, orderItems]);

    useEffect(() => {
        if (cartItems.length === 0) {
            navigate('/cart');
            return;
        }
        if (started.current) return; // StrictMode runs effects twice in dev
        started.current = true;
        startSession(null).then((message) => message && setError(message));
    }, [cartItems.length, navigate, startSession]);

    const elementsOptions = useMemo(() => session && ({
        clientSecret: session.clientSecret,
        elementsOptions: { appearance: stripeAppearance, fonts: stripeFonts },
        defaultValues: buildDefaults(session, carry, guestEmail),
    }), [session, carry, guestEmail]);

    return (
        <div className="rd-checkout">
            <Meta title="Secure checkout" />
            <div className="rd-stripes" aria-hidden="true" />

            <div className="rd-checkout__bar">
                <span className="rd-eyebrow rd-checkout__secure"><LockIcon /> Secure checkout</span>
                <Link to="/cart" className="rd-link">Back to basket</Link>
            </div>

            {error && (
                <div className="rd-checkout__wrap">
                    <div className="rd-alert" role="alert">
                        <strong>Checkout couldn’t start.</strong> {error}{' '}
                        <Link to="/cart" className="rd-link">Return to your basket</Link>
                    </div>
                </div>
            )}

            {!error && !session && <CheckoutSkeleton />}

            {session && (
                // key: a new promo code creates a new session → remount everything
                <CheckoutElementsProvider key={session.clientSecret} stripe={stripePromise} options={elementsOptions}>
                    <CheckoutBody
                        session={session}
                        cartItems={cartItems}
                        signedInEmail={session.customerEmail || null}
                        onApplyPromo={startSession}
                    />
                </CheckoutElementsProvider>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Form + summary — everything that needs the live Checkout object
// ─────────────────────────────────────────────────────────────────────────────
const CheckoutBody = ({ session, cartItems, signedInEmail, onApplyPromo }) => {
    const state = useCheckoutElements();
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [hasExpress, setHasExpress] = useState(false);
    // Stripe only records the address on payment, so keep what's typed for promo re-starts
    const typedAddress = useRef(null);

    if (state.type === 'loading') return <CheckoutSkeleton />;
    if (state.type === 'error') {
        return (
            <div className="rd-checkout__wrap">
                <div className="rd-alert" role="alert">{state.error.message}</div>
            </div>
        );
    }

    const { checkout } = state;
    const total = checkout.total.total;
    const isHeld = session.isPreOrder && session.captureMethod === 'manual';

    const payLabel = submitting
        ? 'Processing…'
        : session.isPreOrder
            ? `${isHeld ? 'Place pre-order' : 'Pay & pre-order'} · ${total.amount}`
            : `Pay ${total.amount}`;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setMessage(null);
        // On success Stripe redirects to /checkout/complete (or to Klarna/PayPal first)
        const result = await checkout.confirm();
        if (result.type === 'error') {
            setMessage(result.error.message);
            setSubmitting(false);
        }
    };

    const handleExpressConfirm = async (event) => {
        setMessage(null);
        const result = await checkout.confirm({ expressCheckoutConfirmEvent: event });
        if (result.type === 'error') setMessage(result.error.message);
    };

    return (
        <main className="rd-checkout__wrap rd-checkout__grid">
            {/* Phones: total and items at the top, instead of below the Pay button */}
            <details className="rd-mobile-summary">
                <summary>
                    <span>Order summary</span>
                    <strong>{total.amount}</strong>
                </summary>
                <OrderSummary checkout={checkout} cartItems={cartItems} className="" headingId="rd-sum-mobile" />
            </details>

            <form className="rd-checkout__main" onSubmit={handleSubmit} noValidate>

                {/* Express */}
                <section aria-labelledby="rd-express" className="rd-section" hidden={!hasExpress}>
                    <h2 id="rd-express" className="rd-eyebrow rd-center">Express checkout</h2>
                    <ExpressCheckoutElement
                        options={{
                            buttonHeight: 48,
                            buttonType: { applePay: 'buy', googlePay: 'buy', paypal: 'checkout' },
                            layout: { maxColumns: 4, maxRows: 1, overflow: 'auto' },
                        }}
                        onReady={({ availablePaymentMethods }) =>
                            setHasExpress(!!availablePaymentMethods && Object.values(availablePaymentMethods).some(Boolean))}
                        onConfirm={handleExpressConfirm}
                    />
                    <div className="rd-divider"><span>or pay below</span></div>
                </section>

                {/* 1 Contact */}
                <section aria-labelledby="rd-s1" className="rd-section">
                    <div className="rd-section__head">
                        <h2 id="rd-s1" className="rd-h2"><span className="rd-num">01</span> Contact</h2>
                        {!signedInEmail && (
                            <span className="rd-muted">Have an account? <Link className="rd-link" to="/login?redirect=/checkout">Log in</Link></span>
                        )}
                    </div>
                    {signedInEmail ? (
                        <p className="rd-signed-in">Signed in as <strong>{signedInEmail}</strong></p>
                    ) : (
                        <ContactDetailsElement />
                    )}
                </section>

                {/* 2 Delivery */}
                <section aria-labelledby="rd-s2" className="rd-section">
                    <h2 id="rd-s2" className="rd-h2"><span className="rd-num">02</span> Delivery</h2>
                    <ShippingAddressElement onChange={(e) => { typedAddress.current = e.value; }} />
                    <div className="rd-delivery">
                        <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 7h11v9H3z" /><path d="M14 10h4l3 3v3h-7" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" />
                        </svg>
                        <div className="rd-delivery__text">
                            <strong>Standard UK delivery</strong>
                            <span className="rd-muted">Tracked · free on orders over £{FREE_SHIPPING_THRESHOLD}</span>
                        </div>
                        <strong className={checkout.total.shippingRate.minorUnitsAmount === 0 ? 'rd-free' : ''}>
                            {checkout.total.shippingRate.minorUnitsAmount === 0 ? 'FREE' : checkout.total.shippingRate.amount}
                        </strong>
                    </div>
                </section>

                {/* Promo code — before payment so the total is final when choosing how to pay */}
                <section aria-label="Promo code" className="rd-section rd-promo-section">
                    <PromoField
                        session={session}
                        getTyped={() => ({ email: checkout.email, shippingAddress: checkout.shippingAddress || typedAddress.current })}
                        onApplyPromo={onApplyPromo}
                    />
                </section>

                {/* 3 Payment */}
                <section aria-labelledby="rd-s3" className="rd-section">
                    <div className="rd-section__head">
                        <h2 id="rd-s3" className="rd-h2"><span className="rd-num">03</span> Payment</h2>
                        <span className="rd-muted rd-inline"><LockIcon size={14} /> Encrypted &amp; processed by Stripe</span>
                    </div>

                    {session.isPreOrder && (
                        <div className="rd-note">
                            {isHeld ? (
                                <>
                                    <strong>Pre-order:</strong> we’ll hold {total.amount} on your payment method and only
                                    take it when your order ships.
                                </>
                            ) : (
                                <>
                                    <strong>Pre-order:</strong> you’ll be charged {total.amount} today and we’ll ship as
                                    soon as it’s released. Cancel any time before dispatch for a full refund.
                                </>
                            )}
                        </div>
                    )}

                    <PaymentElement
                        options={{
                            layout: { type: 'accordion', defaultCollapsed: false, radios: 'always', spacedAccordionItems: true },
                        }}
                    />

                    {message && <div className="rd-alert" role="alert">{message}</div>}

                    <button type="submit" className="rd-cta" disabled={submitting || !checkout.canConfirm}>
                        <LockIcon size={18} /> {payLabel}
                    </button>
                    <p className="rd-fineprint">
                        By placing your order you agree to our <Link className="rd-link" to="/terms">Terms</Link> and{' '}
                        <Link className="rd-link" to="/privacy">Privacy Policy</Link>.
                    </p>
                </section>
            </form>

            <OrderSummary checkout={checkout} cartItems={cartItems} />
        </main>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Promo code — applying one re-prices the order and opens a fresh Stripe session
// ─────────────────────────────────────────────────────────────────────────────
const PromoField = ({ session, getTyped, onApplyPromo }) => {
    const [code, setCode] = useState('');
    const [promoError, setPromoError] = useState('');
    const [applying, setApplying] = useState(false);

    const apply = async (promo) => {
        setApplying(true);
        setPromoError('');
        // Keep the email and address already entered — a promo opens a fresh Stripe session
        const err = await onApplyPromo(promo, getTyped());
        if (err) setPromoError(err);
        setApplying(false);
    };

    if (session.promo) {
        return (
            <div className="rd-promo-applied">
                <span>Promo <strong>{session.promo.code}</strong> applied</span>
                <button type="button" className="rd-link-btn" onClick={() => apply(null)} disabled={applying}>Remove</button>
            </div>
        );
    }

    const submit = () => { if (code.trim() && !applying) apply(code.trim()); };

    return (
        <div className="rd-promo">
            <label htmlFor="rd-promo-code">Promo code</label>
            <div className="rd-promo__row">
                <input
                    id="rd-promo-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                    placeholder="e.g. RETRO10"
                    autoComplete="off"
                    autoCapitalize="characters"
                    aria-invalid={!!promoError}
                    aria-describedby={promoError ? 'rd-promo-error' : undefined}
                />
                <button type="button" onClick={submit} disabled={applying || !code.trim()}>{applying ? '…' : 'Apply'}</button>
            </div>
            {promoError && <span id="rd-promo-error" className="rd-error" role="alert">{promoError}</span>}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Summary — line amounts come from Stripe so they always match the charge
// ─────────────────────────────────────────────────────────────────────────────
const OrderSummary = ({ checkout, cartItems, className = 'rd-summary--aside', headingId = 'rd-sum' }) => {
    const { subtotal, discount, shippingRate, total } = checkout.total;
    const itemCount = checkout.lineItems.reduce((n, li) => n + li.quantity, 0);
    const freeDelivery = shippingRate.minorUnitsAmount === 0;
    const remaining = Math.max(FREE_SHIPPING_THRESHOLD * 100 - subtotal.minorUnitsAmount, 0);
    const progress = Math.min(100, (subtotal.minorUnitsAmount / (FREE_SHIPPING_THRESHOLD * 100)) * 100);

    return (
        <aside className={`rd-summary ${className}`} aria-labelledby={headingId}>
            <div className="rd-summary__head">
                <h2 id={headingId}>Your basket</h2>
                <span className="rd-eyebrow rd-eyebrow--light">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
            </div>

            <div className="rd-summary__body">
                <ul className="rd-items">
                    {checkout.lineItems.map((li, idx) => {
                        const cartItem = cartItems[idx];
                        return (
                            <li key={li.id} className="rd-item">
                                <div className="rd-item__thumb">
                                    {cartItem?.image && <img src={cartItem.image} alt="" />}
                                    <span className="rd-item__qty" aria-label={`Quantity ${li.quantity}`}>{li.quantity}</span>
                                </div>
                                <span className="rd-item__name">{li.name}</span>
                                <span className="rd-item__price">{li.subtotal.amount}</span>
                            </li>
                        );
                    })}
                </ul>

                <div className={`rd-ship ${freeDelivery ? 'rd-ship--done' : ''}`}>
                    {freeDelivery ? (
                        <span className="rd-inline"><CheckIcon /> You’ve unlocked free delivery</span>
                    ) : (
                        <span>Add <strong>{formatGBP(remaining)}</strong> more for free delivery</span>
                    )}
                    <div className="rd-ship__track"><div className="rd-ship__fill" style={{ width: `${freeDelivery ? 100 : progress}%` }} /></div>
                </div>

                <dl className="rd-totals">
                    <div><dt>Subtotal</dt><dd>{subtotal.amount}</dd></div>
                    {discount.minorUnitsAmount > 0 && (
                        <div><dt>Discount</dt><dd className="rd-free">−{discount.amount}</dd></div>
                    )}
                    <div><dt>Delivery</dt><dd className={freeDelivery ? 'rd-free' : ''}>{freeDelivery ? 'FREE' : shippingRate.amount}</dd></div>
                </dl>
                <div className="rd-grand">
                    <span>Total</span>
                    <span><small>GBP</small> {total.amount}</span>
                </div>
            </div>
        </aside>
    );
};

const CheckoutSkeleton = () => (
    <div className="rd-checkout__wrap rd-checkout__grid" aria-busy="true" aria-label="Loading checkout">
        <div className="rd-checkout__main">
            {[180, 260, 320].map((h) => <div key={h} className="rd-skel" style={{ height: h }} />)}
        </div>
        <div className="rd-summary rd-skel" style={{ height: 460 }} />
    </div>
);

export default CheckoutScreen;
